import { Anchor, SegmentedControl, Select, Stack, Table, Text } from '@mantine/core';
import type { RisqStatus } from '@opti-sea/risq';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router';
import { useWorkplace } from '../../../core/access';
import { PageTitle, QueryState, StatusBadge, usePositionNames } from '../../../ui/components';
import { formatDateTime } from '../../../ui/format';
import { useFleetItems } from '../data';

const VIEWS = [
  'office_action',
  'in_progress',
  'not_started',
] as const satisfies readonly RisqStatus[];
type View = (typeof VIEWS)[number];

function isView(value: string | null): value is View {
  return value !== null && (VIEWS as readonly string[]).includes(value);
}

/**
 * D6 Open items of all vessels. First view "Waiting for office" (Office action: the crew did its
 * part and sent a request — owner, 2026-11-03), then In progress and Not started.
 */
export function WaitingPage() {
  const { t } = useTranslation(['dashboard', 'common']);
  const { vessels } = useWorkplace();
  const { byVessel, loading, error } = useFleetItems();
  const { list } = usePositionNames();
  const [params, setParams] = useSearchParams();
  const view: View = isView(params.get('view')) ? (params.get('view') as View) : 'office_action';
  const vesselFilter = params.get('vessel');
  const update = (key: string, value: string | null) => {
    const next = new URLSearchParams(params);
    if (value === null) {
      next.delete(key);
    } else {
      next.set(key, value);
    }
    setParams(next, { replace: true });
  };
  const rows = vessels
    .filter((v) => vesselFilter === null || v.id === vesselFilter)
    .flatMap((v) =>
      (byVessel.get(v.id) ?? [])
        .filter((i) => i.status === view)
        .map((i) => ({ vessel: v, item: i })),
    )
    .sort((a, b) => (b.item.statusChangedAt ?? '').localeCompare(a.item.statusChangedAt ?? ''));

  return (
    <>
      <PageTitle title={t('waiting.title')} />
      <Stack>
        <Text>{t('waiting.intro')}</Text>
        <SegmentedControl
          value={view}
          onChange={(value) => update('view', value === 'office_action' ? null : value)}
          data={VIEWS.map((v) => ({
            value: v,
            label: v === 'office_action' ? t('waiting.title') : t(`common:status.${v}`),
          }))}
          aria-label={t('waiting.view')}
        />
        <Select
          label={t('waiting.vessel')}
          clearable
          placeholder={t('waiting.allVessels')}
          data={vessels.map((v) => ({ value: v.id, label: v.name }))}
          value={vesselFilter}
          onChange={(value) => update('vessel', value)}
          maw={320}
        />
        <QueryState loading={loading} error={error}>
          {() =>
            rows.length === 0 ? (
              <Text>{view === 'office_action' ? t('waiting.empty') : t('waiting.emptyOther')}</Text>
            ) : (
              <Table.ScrollContainer minWidth={800}>
                <Table striped verticalSpacing="sm">
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>{t('fleet.vessel')}</Table.Th>
                      <Table.Th>{t('questions.code')}</Table.Th>
                      <Table.Th>{t('questions.text')}</Table.Th>
                      <Table.Th>{t('questions.responsible')}</Table.Th>
                      <Table.Th>{t('waiting.since')}</Table.Th>
                      <Table.Th>{t('waiting.lastNote')}</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {rows.map(({ vessel, item }) => (
                      <Table.Tr key={item.id}>
                        <Table.Td>{vessel.name}</Table.Td>
                        <Table.Td>
                          <Anchor
                            component={Link}
                            to={`/vessels/${vessel.id}/questions/${item.id}`}
                            fw={600}
                          >
                            {item.question.code}
                          </Anchor>
                        </Table.Td>
                        <Table.Td maw={380}>
                          <Text size="sm" lineClamp={2}>
                            {item.question.textEn}
                          </Text>
                        </Table.Td>
                        <Table.Td>{list(item.positions.codes, t('questions.unassigned'))}</Table.Td>
                        <Table.Td>
                          <StatusBadge status={item.status} />
                          <Text size="xs">
                            {item.statusChangedAt === null
                              ? ''
                              : formatDateTime(item.statusChangedAt)}
                          </Text>
                        </Table.Td>
                        <Table.Td maw={260}>
                          <Text size="sm" lineClamp={3}>
                            {item.lastNote ?? ''}
                          </Text>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </Table.ScrollContainer>
            )
          }
        </QueryState>
      </Stack>
    </>
  );
}
